import React from 'react';
import { useState, useRef } from "react";
import './Styles/global.css';
import styles from './Styles/Welcome.module.css'
import { Link, useParams } from "react-router-dom";

// Function to check if content contains HTML => will be used to apply the dangerouslySetInnerHTML for safe rendering if necessary
export const containsHTML = (content) => {
  return typeof content === 'string' && /<[^>]+>/.test(content);
};

const ImageContainer = ({ sectionImg, style = styles.sectionImg }) => {
  return <>
    {sectionImg?.src && (
      Array.isArray(sectionImg.src) ? (
        <picture>
          {/* WebP Source */}
          <source
            srcSet={sectionImg.src[0]}
            type="image/webp"
            style={{
              width: sectionImg.width,
              height: sectionImg.height
            }}
          />
          {/* Fallback Source */}
          <img
            src={sectionImg.src[1]} // Fallback image (JPG/PNG)
            alt={sectionImg.alt || ""}
            width={sectionImg.width}
            height={sectionImg.height}
            className={style}
            loading="lazy"
          />
        </picture>
      ) : (
        // Single image source
        <>
          <img
            src={sectionImg.src}
            alt={sectionImg.alt || ""}
            width={sectionImg.width}
            height={sectionImg.height}
            className={style}
            loading="lazy"
            style={sectionImg.style}
          />
          {sectionImg.caption && <p className={styles[sectionImg.captionStyle] ?? styles.imageCaption} dangerouslySetInnerHTML={{ __html: sectionImg.caption }} />}
        </>
      )
    )}
  </>
}

export const IterationContainer = ({ text, EnumerateTag, extraSpaceBefore = "0rem", extraSpaceAfter = "0rem", className, divClassName }) => {
  if (text === undefined)
    return <></>
  return <>
    <div style={{ height: extraSpaceBefore }} />
    {Array.isArray(text) ? (
      <ul>
        {text.map((item, index) => (
          Array.isArray(item) ? (
            <div key={index} className={divClassName}>
              <p className={className}><strong>{item[0]}</strong></p><br />
              <ul>
                {item.slice(1).map((item2, index2) => (
                  <EnumerateTag className={className} key={index2} dangerouslySetInnerHTML={{ __html: item2 }} />
                ))}
              </ul>
              <p>&nbsp;</p>
            </div>
          ) :
            containsHTML(item) ? (
              <EnumerateTag className={className} key={index} dangerouslySetInnerHTML={{ __html: item }} />
            ) : (
              <EnumerateTag className={className} key={index}>{item}</EnumerateTag>
            )
        ))}
      </ul>
    ) : containsHTML(text) ? (
      <div className={divClassName}><p className={className} dangerouslySetInnerHTML={{ __html: text }} /></div>
    ) : (
      <div className={divClassName}><p className={className}>{text}</p></div>
    )}
    <div style={{ height: extraSpaceAfter }} />
  </>
}

export const Header = ({ sectionKey, sectionContent, sectionColor, EnumerateTag = 'li' }) => {
  return (
    <section key={sectionKey} className={`${styles[sectionColor]}`}>
      <div className={`${styles.sectionWrapper2} ${styles.sectionHero}`}>
        {sectionContent.img.src && <div className={styles.heroImg} style={{
          backgroundImage: sectionContent.img.src ? `url(${sectionContent.img.src})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}>
          {sectionContent.img.caption && <p className={styles.imageCaptionTop} dangerouslySetInnerHTML={{ __html: sectionContent.img.caption }} />}
        </div>}
        <div className={`${styles.contentContainer} ${styles[sectionKey]}`}>
          <span className={styles.subtitle}>{sectionContent.subtitle}</span>
          <h1>{sectionContent.h1}</h1>
          <IterationContainer
            text={sectionContent.text}
            EnumerateTag={EnumerateTag}
            extraSpaceBefore='3rem'
            extraSpaceAfter='2rem' />
        </div>
      </div>
    </section>
  )
}

export const WideImage = ({ sectionKey, sectionContent, sectionColor }) => {
  return (
    <section key={sectionKey} className={`${styles[sectionColor]}`}>
      <div className={`${styles.sectionWrapper2} ${styles.sectionHero}`}>
        <div className={styles.wideImg} style={{
          backgroundImage: (sectionContent.img.src) ? `url(${sectionContent.img.src})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}>
          {sectionContent.img.caption && <p className={styles.imageCaptionRight} dangerouslySetInnerHTML={{ __html: sectionContent.img.caption }} />}
        </div>
      </div>
    </section>
  )
}

// Element Line: Default element. Text + Image + Colored bar with button (2nd text only works if it doesn't wrap)

export const Line = ({ sectionKey, sectionContent, sectionColor, alignLeft, EnumerateTag = 'li' }) => {
  const { lang } = useParams();
  const sectionClass = (alignLeft) ? styles.sectionEven : styles.sectionOdd
  const twoImages = "img2" in sectionContent
  const imageCSS = `${styles.sectionImageContainer} ${(twoImages) ? styles.twoImages : ''}`
  const sectionImageSecondImageContainer = (alignLeft) ? styles.sectionImageSecondImageContainerRight : styles.sectionImageSecondImageContainerLeft
  return (
    <section key={sectionKey} className={`${styles[sectionColor]}`}>
      <div className={`${styles.sectionWrapper1} ${sectionClass}`}>
        <div className={styles.spacer} style={{ width: "10%", backgroundColorx: "yellow" }}>&nbsp;</div>
        <div className={styles.sectionTextContainer} style={{ backgroundColorx: "red" }}>
          {sectionContent.h2 && <h2>{sectionContent.h2}</h2>}
          <IterationContainer text={sectionContent.text} EnumerateTag={EnumerateTag} />
          <br /><br />
          {sectionContent.buttonText &&
            <div className={styles.buttonWrapper}>
              <Link to={
                (sectionContent.buttonLink.startsWith("/")) ? sectionContent.buttonLink : `/${lang}/${sectionContent.buttonLink}`
              } className={styles.button} style={{ zIndex: "100" }} {...(sectionContent.externalLink && { target: "_blank", rel: "noopener noreferrer" })}>
                {sectionContent.buttonText}
              </Link>
            </div>
          }
          {sectionContent.text2 && <div className={styles.lowerTextWrapper}>
              <IterationContainer text={sectionContent.text2} EnumerateTag={EnumerateTag} divClassName={styles.lowerText}/>
            </div>
          }
        </div>
        <div className={styles.spacer} style={{ width: "5%", backgroundColorx: "orange" }}>&nbsp;</div>
        <div className={imageCSS}><ImageContainer sectionImg={sectionContent.img} /></div>
        {twoImages || <div className={styles.spacer} style={{ width: "15%", backgroundColorx: "red" }}>&nbsp;</div>}
      </div>
      <div className={`${styles.sectionWrapper2} ${sectionClass}`}>
        <div className={styles.sectionBackground} style={{ backgroundImage: `url('/Img/Background/background-${sectionColor}.png')` }}>
          {twoImages &&
            <div className={sectionImageSecondImageContainer}>
              <ImageContainer sectionImg={sectionContent.img2} />
            </div>
          }
        </div>
        <div className={styles.sectionImageContainerMobile} style={{ marginTop: "-100px" }}>
          <ImageContainer sectionImg={sectionContent.img} />
        </div>
        {twoImages && <div className={styles.sectionImageContainerMobile}>
          <ImageContainer sectionImg={sectionContent.img2} />
        </div>}
        <div className={styles.spacer} style={{ width: "10%", backgroundColorx: "orange" }}>&nbsp;</div>
      </div>
      {twoImages && <div style={{ height: "100px" }}></div>}
    </section>
  );
}

// Element Line2: Default element. Text + Image + Colored bar with button + 2nd text

export const Line2 = ({ sectionKey, sectionContent, sectionColor, alignLeft, EnumerateTag = 'li' }) => {
  const { lang } = useParams();
  const sectionClass = (alignLeft) ? styles.sectionEven2 : styles.sectionOdd2
  return (
    <section key={sectionKey} className={`${styles[sectionColor]}`}>
      <div className={`${styles.sectionWrapper1} ${sectionClass}`}>
        <div style={{display: "flex", width: '90%'}} className={sectionClass}>
          <div className={styles.spacer} style={{ width: "11%", backgroundColorx: "yellow" }}>&nbsp;</div>
          <div className={styles.sectionTextContainer2Upper} style={{ backgroundColorx: "red" }}>
            {sectionContent.h2 && <h2>{sectionContent.h2}</h2>}
            <IterationContainer text={sectionContent.text} EnumerateTag={EnumerateTag} divClassName={styles.upperText} />
            {sectionContent.buttonText &&
              <div className={styles.buttonWrapper}>
                <Link to={
                  (sectionContent.buttonLink.startsWith("/")) ? sectionContent.buttonLink : `/${lang}/${sectionContent.buttonLink}`
                } className={styles.button} style={{ zIndex: "100" }} {...(sectionContent.externalLink && { target: "_blank", rel: "noopener noreferrer" })}>
                  {sectionContent.buttonText}
                </Link>
              </div>
            }
          </div>
          <div className={styles.spacer} style={{ width: "6%", backgroundColorx: "orange" }}>&nbsp;</div>
          <div className={styles.sectionImageContainer2}><ImageContainer sectionImg={sectionContent.img} /></div>
        </div>
        <div className={styles.spacer} style={{ width: "10%", backgroundColorx: "orange" }}>&nbsp;</div>
      </div>
      <div className={`${styles.sectionWrapper2} ${sectionClass}`}>
        <div className={`${styles.sectionBackground} ${sectionClass}`} style={{ backgroundImage: `url('/Img/Background/background-${sectionColor}.png')`}}>
          <div className={styles.spacer} style={{ width: "11%", background: 'transparent', backgroundColorx: "yellow" }}>&nbsp;</div>
          <div className={`${styles.sectionTextContainer2Lower} ${styles.lowerTextWrapper}`} style={{ backgroundColorx: "red" }}>
            {sectionContent.text2 && <IterationContainer text={sectionContent.text2} divClassName={styles.lowerText} EnumerateTag={EnumerateTag} />
            }
          </div>
          <div className={styles.spacer} style={{ width: "6%", background: 'transparent', backgroundColorx: "orange" }}>&nbsp;</div>      
          <div className={styles.sectionImageContainer2Text}><p style={{fontSize: "18px"}} dangerouslySetInnerHTML={{ __html: sectionContent.subtitle }}></p></div>
        </div>
        <div className={styles.sectionImageContainerMobile}>
          <ImageContainer sectionImg={sectionContent.img} />
        </div>
        <div className={styles.sectionImageContainer2TextMobile}>
          <p style={{fontSize: "18px"}} dangerouslySetInnerHTML={{ __html: sectionContent.subtitle }}></p>
        </div>
        <div className={styles.spacer} style={{ width: "10%", backgroundColorx: "orange" }}>&nbsp;</div>
      </div>
    </section>
  );
}

// Element SingleColumn: text box without background with the provided width

export const SingleColumn = ({ sectionKey, sectionContent, sectionColor, alignLeft, EnumerateTag = 'li' }) => {
  const sectionClass = (alignLeft) ? styles.sectionEven : styles.sectionOdd
  const width1 = ("width" in sectionContent) ? sectionContent.width : "35"
  const width2 = 90 - width1
  return (
    <section key={sectionKey} className={`${styles[sectionColor]}`}>
      <div className={`${styles.sectionWrapper1} ${sectionClass}`}>
        <div className={styles.spacer} style={{ width: "10%", backgroundColorx: "yellow" }}>&nbsp;</div>
        <div style={{ backgroundColorx: "red", widthx: `${width1}%` }}>
          {"h2" in sectionContent && <h2>{sectionContent.h2}</h2>}
          <IterationContainer text={sectionContent.text} EnumerateTag={EnumerateTag} className={styles[sectionContent.class]} />
        </div>
        <div className={styles.spacer} style={{ width: `${width2}%`, minWidth: `${width2}%`, backgroundColorx: "orange" }}>&nbsp;</div>
      </div>
      <div style={{ height: "4rem" }}></div>
    </section>
  );
}

// Element Box: colored box with text

export const Box = ({ sectionKey, sectionContent, sectionColor, alignLeft, sectionBackgroundCss, textAlign, EnumerateTag = 'li' }) => {
  const sectionClass = (alignLeft) ? styles.sectionEven : styles.sectionOdd
  const sectionBackgroundText = (sectionBackgroundCss) ? styles[sectionBackgroundCss] : styles.sectionBackgroundText
  return (
    <section key={sectionKey} className={`${styles[sectionColor]}`}>
      <div className={`${styles.sectionWrapper2} ${sectionClass}`}>
        <div className={styles.coloredBox} style={{ backgroundImage: `url('/Img/Background/background-${sectionColor}.png')`, textAlign: textAlign }}>
          <div style={{ background: "transparent", padding: "5rem" }}>
            {"h2" in sectionContent && <p className={styles.boxtitle}>{sectionContent.h2}</p>}
            <IterationContainer text={sectionContent.text} EnumerateTag={EnumerateTag} className={sectionBackgroundText} />
            {"footer" in sectionContent &&
              <>
                <div style={{ height: "2rem", backgroundColor: "transparent" }}></div>
                <p className={styles.sectionBackgroundText} dangerouslySetInnerHTML={{ __html: sectionContent.footer }}></p>
              </>
            }
          </div>
        </div>
        <div className={styles.spacer} style={{ width: "10%", backgroundColorx: "orange" }}>&nbsp;</div>
      </div>
    </section>
  );
}

// Element BoxImage: Image + colored box with text

export const BoxImage = ({ sectionKey, sectionContent, sectionColor, alignLeft, EnumerateTag = 'li' }) => {
  const { lang } = useParams();
  const sectionClass = (alignLeft) ? styles.sectionEven : styles.sectionOdd
  return (
    <section key={sectionKey} className={`${styles[sectionColor]}`}>
      <div className={`${styles.sectionWrapper1} ${sectionClass}`}>
        <div className={styles.spacer} style={{ width: "10%", backgroundColorx: "yellow" }}>&nbsp;</div>
        <div className={styles.sectionTextContainer} style={{ backgroundColorx: "red" }}></div>
        <div className={styles.spacer} style={{ width: "15%", backgroundColorx: "orange" }}>&nbsp;</div>
        <div className={styles.sectionImageContainer}><ImageContainer sectionImg={sectionContent.img} /></div>
        <div className={styles.sectionImageContainerMobile}><ImageContainer sectionImg={sectionContent.img} /></div>
        <div className={styles.spacer} style={{ width: "5%", backgroundColorx: "red" }}>&nbsp;</div>
      </div>
      <div className={`${styles.sectionWrapper2} ${sectionClass}`}>
        <div className={`${styles.coloredBoxImage}`} style={{ backgroundImage: `url('/Img/Background/background-${sectionColor}.png')` }}>
          <div className={styles.spacer} style={{ width: "40%", backgroundColor: "transparent" }}>&nbsp;</div>
          <div style={{ background: "transparent", padding: "5rem", width: "60%" }}>
            {"h2" in sectionContent && <p className={styles.boxtitle}>{sectionContent.h2}</p>}
            {sectionContent.text && <IterationContainer text={sectionContent.text} EnumerateTag={EnumerateTag} className={styles.sectionBackgroundText} />}
            {sectionContent.textbutton &&
              <ul>
                {sectionContent.textbutton.map((item, index) => (
                  <EnumerateTag className={styles.boxImage} key={index}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", backgroundColor: "transparent", width: "100%" }}>
                      <div style={{ backgroundColor: "transparent" }}>{item.text}</div>
                      {
                        (() => {
                          const LinkTag = (item.buttonLink === '') ? 'span' : Link;
                          return <LinkTag to={
                            (item.buttonLink.startsWith("/") || item.buttonLink.startsWith("http")) ? item.buttonLink : `/${lang}/${item.buttonLink}`
                          } {...((item.buttonLink.startsWith("/") || item.buttonLink.startsWith("http")) && { target: "_blank", rel: "noopener noreferrer" })} className="red-button" style={{ zIndex: "100", paddingTop: "0px", paddingBottom: "0px" }}
                            title={item.tooltip}
                            >
                            {item.buttonText}
                          </LinkTag>
                        })()
                      }
                    </div>
                  </EnumerateTag>
                ))
                }
              </ul>
            }
            {"footer" in sectionContent &&
              <>
                <div style={{ height: "2rem", backgroundColor: "transparent" }}></div>
                <p className={styles.sectionBackgroundText} dangerouslySetInnerHTML={{ __html: sectionContent.footer }}></p>
              </>
            }
          </div>
        </div>
        <div className={styles.spacer} style={{ width: "10%", backgroundColorx: "orange" }}>&nbsp;</div>
      </div>
    </section >
  );
}

// Element ImageArray: Images next to each other

export const ImageArray = ({ sectionKey, sectionContent, sectionColor, alignLeft }) => {
  const sectionClass = (alignLeft) ? styles.sectionEven : styles.sectionOdd
  return (
    <section key={sectionKey} className={`${styles[sectionColor]}`}>
      <div style={{ height: "1rem" }}></div>
      <div className={`${sectionClass}`}>
        <div className={styles.sectionBackground} style={{ backgroundImage: `url('/Img/Background/background-${sectionColor}.png')` }} />
        <div className={styles.spacer} style={{ width: "10%", backgroundColorx: "orange" }}>&nbsp;</div>
      </div>
      <div className={styles.imageArrayContainer}>
        {sectionContent.images.map((item, index) => (<ImageContainer key={index} sectionImg={item} style={styles.imageArray} />))}
      </div>
    </section>
  );
}

// Element TwoColumn: Two text boxes (no background)

export const TwoColumn = ({ sectionKey, sectionContent, sectionColor, EnumerateTag = 'li', ...props }) => {
  return (
    <section key={sectionKey} className={`${styles[sectionColor]}`}>
      <div className={`${styles.twoColumnWrapper}`}>
        <div className={styles.spacer} style={{ width: "10%", backgroundColorx: "yellow" }}>&nbsp;</div>
        <div className={styles.twoColumnTextContainer} style={{ backgroundColorx: "red" }}>
          {sectionContent.h2 && <h2>{sectionContent.h2}</h2>}
          <IterationContainer text={sectionContent.text[0]} EnumerateTag={EnumerateTag} />
        </div>
        <div className={styles.spacer} style={{ width: "10%", backgroundColorx: "orange" }}>&nbsp;</div>
        {sectionContent.text[1] &&
          <div className={styles.twoColumnTextContainer} style={{ backgroundColorx: "red" }}>
            <IterationContainer text={sectionContent.text[1]} EnumerateTag={EnumerateTag} />
            {sectionContent.button2Text &&
              <div className={styles.buttonWrapper}>
                <Link onClick={props.button_target} className={styles.buttonOnWhite} style={{ zIndex: "100" }}>
                  {sectionContent.button2Text}
                </Link>
              </div>
            }
          </div>
        }
        {sectionContent.img &&
          <div className={styles.twoColumnImageContainer} style={{ backgroundColorx: "red" }}>
            <ImageContainer sectionImg={sectionContent.img} />
          </div>
        }
        <div className={styles.spacer} style={{ width: "10%", backgroundColorx: "orange" }}>&nbsp;</div>
      </div>
      <div style={{ height: "4rem" }}></div>
    </section>
  )
}

const AccordionElement = ({ header, children }) => {
  const [isActive, setIsActive] = useState(false);
  const panelRef = useRef(null);

  const handleClick = () => {
    setIsActive((prev) => !prev);

    if (panelRef.current) {
      if (panelRef.current.style.maxHeight) {
        panelRef.current.style.maxHeight = null;
      } else {
        panelRef.current.style.maxHeight = panelRef.current.scrollHeight + "px";
      }
    }
  };

  return (
    <>
      <button
        className={`${styles.accordion} ${styles.text} ${isActive ? styles.active : ""}`}
        onClick={handleClick}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "transparent" }}>
          <p>{header}</p>
          <p style={{ fontSize: "35px", marginTop: "-15px" }}>⌄</p>
        </div>
      </button>
      <div ref={panelRef} className={styles.panel}>
        <div style={{ height: "2rem" }}></div>
        {children}
        <div style={{ height: "2rem" }}></div>
      </div>
    </>
  );
};

export const Accordion = ({ sectionKey, sectionContent, sectionColor, alignLeft }) => {
  const sectionClass = (alignLeft) ? styles.sectionEven : styles.sectionOdd
  const width1 = ("width" in sectionContent) ? sectionContent.width : "35"
  const width2 = 90 - width1
  return (
    <section key={sectionKey} className={`${styles[sectionColor]}`}>
      <div className={`${styles.sectionWrapper1} ${sectionClass}`}>
        <div className={styles.spacer} style={{ width: "10%", backgroundColorx: "yellow" }}>&nbsp;</div>
        <div className={styles.sectionTextContainer} style={{ backgroundColorx: "red", width: `${width1}%` }}>
          <h2>{sectionContent.h2}</h2>
          <div>
            {sectionContent.elements.map((item, index) => (
              <AccordionElement key={index} header={item.header}>
                <div style={{ display: "flex", justifyContent: "space-around", alignItems: "center" }}>
                  <div style={{ width: "60%" }}><p>{item.text}</p></div>
                  {item.images && <div style={{ width: "fit-content", textAlign: "center" }}>{
                    item.images.map((img, index2) => (
                      <div key={index2}><ImageContainer sectionImg={img} style={styles.accordionImage} /><br />{img.subtitle}<br /><br /></div>))}
                  </div>}
                </div>
              </AccordionElement>
            ))}
          </div>
        </div>
        <div className={styles.spacer} style={{ width: `${width2}%`, backgroundColorx: "orange" }}>&nbsp;</div>
      </div>
      <div style={{ height: "4rem" }}></div>
    </section>
  )
}
